/**
 * SectionCard - one section of a summary page: a card whose header holds
 * the title, an optional one-line description, and the section's actions.
 *
 * Child components put their buttons in the header with <SectionActions>:
 * inside a SectionCard they are portalled into the header's action slot;
 * anywhere else (e.g. a page that renders the component on its own) they
 * render inline, right aligned, as before.
 */
import React, { createContext, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Card, Divider, Typography } from '@mui/material';

const ActionSlotContext = createContext<HTMLElement | null | undefined>(undefined);

export interface SectionCardProps {
  title: string;
  /** One short line under the title: what this section is for. */
  subtitle?: React.ReactNode;
  /** Header actions owned by the host page. */
  action?: React.ReactNode;
  /** Status shown next to the title (e.g. a chip). */
  status?: React.ReactNode;
  children: React.ReactNode;
  /** Remove the body padding (for edge-to-edge tables or charts). */
  flush?: boolean;
  'data-testid'?: string;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title, subtitle, action, status, children, flush, ...rest
}) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const headingId = `section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <Card
      variant="outlined"
      component="section"
      aria-labelledby={headingId}
      sx={{ borderRadius: 2, overflow: 'visible' }}
      data-testid={rest['data-testid']}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          px: { xs: 2, sm: 2.5 },
          pt: 2,
          pb: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography id={headingId} variant="subtitle1" component="h3" fontWeight={600} lineHeight={1.3}>
              {title}
            </Typography>
            {status}
          </Box>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box ref={setSlot} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0, '&:empty': { display: 'none' } }}>
          {action}
        </Box>
      </Box>
      <Divider />
      <Box sx={flush ? undefined : { px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <ActionSlotContext.Provider value={slot}>{children}</ActionSlotContext.Provider>
      </Box>
    </Card>
  );
};

/** Buttons a section's content wants in its card header. */
export const SectionActions: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const slot = useContext(ActionSlotContext);
  if (slot === undefined) {
    // Not inside a SectionCard: inline, right aligned.
    return <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>{children}</Box>;
  }
  // Inside a SectionCard: wait for the header slot to mount, then portal into it.
  return slot ? createPortal(children, slot) : null;
};

/** Label / value pair for a properties list. */
export const PropertyRow: React.FC<{ label: string; value?: React.ReactNode; last?: boolean }> = ({ label, value, last }) => (
  <Box
    component="div"
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '150px 1fr', lg: '1fr' },
      columnGap: 2,
      rowGap: 0.25,
      py: 1.25,
      borderBottom: last ? 0 : 1,
      borderColor: 'divider',
    }}
  >
    <Typography component="dt" variant="caption" color="text.secondary" sx={{ fontWeight: 500, letterSpacing: '0.02em' }}>
      {label}
    </Typography>
    <Typography component="dd" variant="body2" fontWeight={500} sx={{ m: 0, overflowWrap: 'anywhere' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

export default SectionCard;
